import { type FC, useMemo } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { newrelic } from "@jti/fe-clients";
import { getSessionStorage } from "@jti/rest-api";
import { useSelector } from "@jti/store/reduxHooks";
import { useTranslations } from "@jti/ui";

import { useProductsMappedToSummaryItems } from "@checkout-v2/features/ItemsSummary/hooks";
import { LOYALTY_CART_POINTS_STORAGE_KEY } from "@checkout-v2/features/LoyaltyPoints/utils";
import { useCartItems } from "@checkout-v2/shared";
import { useCartPrices } from "@checkout-v2/shared/hooks/useCartPrices";
import { useLoyaltyActiveStatus } from "@checkout-v2/shared/hooks/useLoyaltyActiveStatus";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";

import type { PaymentSummaryViewProps } from "../../components/PaymentSummaryView";
import { PaymentSummaryView } from "../../components/PaymentSummaryView";
import { getFormattedPaymentDiscounts } from "../../utils/getFormattedPaymentDiscounts";

type CartPaymentSummaryContainerProps = {
  showLoyaltyPointsSummary?: boolean;
};

const sessionStorage = getSessionStorage();

export const CartPaymentSummaryContainer: FC<CartPaymentSummaryContainerProps> = ({
  showLoyaltyPointsSummary = false,
}) => {
  const { __ } = useTranslations();
  const { data: prices, isLoading, isUninitialized, isSuccess, isError, error } = useCartPrices();
  const isLoyaltyActive = useLoyaltyActiveStatus();
  const { loyaltyChoice } = useSelector((store) => store.checkoutV2.loyaltyPointsState);
  const checkoutConfiguration = useCheckoutV2Configuration();
  const { showPricesWithTaxExcluded = false } = useConfig();

  const { data: cartItems } = useCartItems();
  const { summaryItems } = useProductsMappedToSummaryItems(cartItems);

  const discounts = useMemo(() => getFormattedPaymentDiscounts(prices?.discounts, __), [prices?.discounts, __]);

  if (isUninitialized || isLoading) {
    return <PaymentSummaryView isLoading={true} title={__("cart.payment_summary.title")} />;
  }

  if ((isSuccess && !prices) || isError || !prices) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error in PaymentSummaryContainer";
    newrelic.noticeError(new Error(errorMessage));
    return null;
  }

  const { subtotalIncludingTax, appliedTaxes, grandTotal, shipping, estimatedShippingCost, appliedCampaign, subtotal } =
    prices;

  const cartPoints = sessionStorage.get(LOYALTY_CART_POINTS_STORAGE_KEY, 0);
  const showLoyalty = showLoyaltyPointsSummary && isLoyaltyActive;

  const priceToUse = showPricesWithTaxExcluded ? subtotal : subtotalIncludingTax;

  const data: PaymentSummaryViewProps = {
    title: __("cart.payment_summary.title"),
    isLoading: false,
    subtotal: priceToUse,
    shipping: shipping ?? estimatedShippingCost,
    total: grandTotal,
    appliedTaxes,
    taxValue: appliedTaxes?.length
      ? {
          value: appliedTaxes.reduce((sum, tax) => sum + tax.amount.value, 0),
          currency: grandTotal?.currency || "",
        }
      : undefined,
    taxMessage: checkoutConfiguration.taxInfo?.visible ? checkoutConfiguration.taxInfo.message : undefined,
    discounts,
    loyaltyDiscount: appliedCampaign?.loyaltyDiscountUuid
      ? {
          label: __("cart.prices.loyalty.points_redemption"),
          value: appliedCampaign.loyaltyDiscountAmount,
          currency: priceToUse?.currency ?? "",
        }
      : undefined,

    loyaltyPoints: showLoyalty
      ? {
          totalLoyaltyPoints: cartPoints,
          loyaltyChoice: loyaltyChoice || 0,
          learnMoreUrl: checkoutConfiguration?.loyalty?.learnMoreUrl,
        }
      : undefined,
    summaryItems,
    showBackToCartButton: checkoutConfiguration.paymentSummaryConfig?.isEnabledBackToCartButton ?? false,
  };

  return <PaymentSummaryView {...data} />;
};
